import { useEffect, useMemo } from 'react';

import { GlobalPropertiesTrackingProperties } from '$src/business/atoms/global-properties-track';
import { AuthStatuses } from '$src/hooks/auth';
import { useCart, useShop } from '$src/hooks/queries';
import { useAuthStateAtom } from '$src/hooks/state/useAuthStateAtom';
import { useROFormMessageAtom } from '$src/hooks/state/useFormMessageAtom';
import { useGlobalPropertiesTrackAtom } from '$src/hooks/state/useGlobalPropertiesTrackAtom';
import { usePaymentMethodsAtom } from '$src/hooks/state/usePaymentMethodsAtom';
import { getEffectiveCheckoutTheme } from '$src/utils/themeColors';

import { useTrackAnalytics } from '../useTrackAnalytics';
import { getCartTrackingProperties } from './get-cart-tracking-properties';

export const useGlobalPropertiesTracking = () => {
  const authState = useAuthStateAtom();
  const { data: cart } = useCart();
  const { selectedPaymentMethod } = usePaymentMethodsAtom();
  const { data: shop } = useShop();
  const message = useROFormMessageAtom();
  const { onGlobalPropertiesChange } = useGlobalPropertiesTrackAtom();
  const { setEventsProperties, getPosthogSessionUrl } = useTrackAnalytics();

  const globalPropertiesTrackingProperties = useMemo<GlobalPropertiesTrackingProperties>(() => {
    const posthogSessionUrl = getPosthogSessionUrl();
    const checkoutTheme = getEffectiveCheckoutTheme(shop);

    return {
      shopName: shop?.name ?? message?.shopName,
      shopId: message?.shopId,
      language: message?.language,
      cartId: message?.cart?.id,
      platform: shop?.platformType,
      source: message?.source,
      analyticsSource: message?.analytics_source,
      checkoutTheme,
      posthogSessionUrl,
      isAuthenticated: authState === AuthStatuses.AUTHENTICATED,
      ...(getCartTrackingProperties({ cart, selectedPaymentMethod }) ?? {}),
    };
  }, [cart, selectedPaymentMethod, shop, message, getPosthogSessionUrl, authState]);

  useEffect(() => {
    onGlobalPropertiesChange(globalPropertiesTrackingProperties);
    setEventsProperties({ ...globalPropertiesTrackingProperties });
  }, [globalPropertiesTrackingProperties, onGlobalPropertiesChange, setEventsProperties]);
};
